import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, UserPlus, Archive, ArchiveRestore, Pencil } from "lucide-react";
import { bookingRequest } from "./bookingApi";

const EMPTY_FORM = { first_name: "", last_name: "", email: "", phone: "", date_of_birth: "", notes: "" };

const PatientsTab = ({ basePath, token }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bookingRequest(basePath, token, "GET", "/patients", {
        params: {
          search: search || undefined,
          include_archived: showArchived ? "true" : undefined,
          limit: 100,
        },
      });
      setPatients(data.items || []);
    } catch (err) {
      toast.error(err.message || "Failed to load patients");
    } finally {
      setLoading(false);
    }
  }, [basePath, token, search, showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (patient) => {
    setEditingId(patient.id);
    setForm({
      first_name: patient.first_name || "",
      last_name: patient.last_name || "",
      email: patient.email || "",
      phone: patient.phone || "",
      date_of_birth: patient.date_of_birth || "",
      notes: patient.notes || "",
    });
    setFormError("");
    setDialogOpen(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    // Mirrors the backend's PatientCreate model_validator so the error
    // shows up before a round trip, not just after a 422.
    if (!form.email && !form.phone) {
      setFormError("A patient needs at least one of email or phone");
      return;
    }
    setFormError("");
    setSaving(true);
    try {
      const payload = { ...form, date_of_birth: form.date_of_birth || null };
      if (editingId) {
        await bookingRequest(basePath, token, "PUT", `/patients/${editingId}`, { body: payload });
        toast.success("Patient updated");
      } else {
        await bookingRequest(basePath, token, "POST", "/patients", { body: payload });
        toast.success("Patient added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      setFormError(err.message || "Could not save patient");
    } finally {
      setSaving(false);
    }
  };

  const archive = async (patient) => {
    if (!window.confirm(`Archive ${patient.first_name} ${patient.last_name}?`)) return;
    try {
      await bookingRequest(basePath, token, "DELETE", `/patients/${patient.id}`);
      toast.success("Patient archived");
      load();
    } catch (err) {
      toast.error(err.message || "Could not archive patient");
    }
  };

  const restore = async (patient) => {
    try {
      await bookingRequest(basePath, token, "POST", `/patients/${patient.id}/restore`);
      toast.success("Patient restored");
      load();
    } catch (err) {
      toast.error(err.message || "Could not restore patient");
    }
  };

  return (
    <Card className="glass-panel border-white/10" data-testid="patients-tab">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-3 h-4 w-4 text-white/40" />
            <Input
              placeholder="Search patients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
              data-testid="patient-search-input"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived-patients" />
            Show archived
          </label>
          <Button className="primary-btn" onClick={openCreate} data-testid="add-patient-btn">
            <UserPlus size={16} />
            Add Patient
          </Button>
        </div>

        {loading ? (
          <p className="text-white/60 text-sm">Loading patients...</p>
        ) : patients.length === 0 ? (
          <p className="text-white/60 text-sm py-8 text-center">No patients yet for this clinic.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-white/60">Name</TableHead>
                <TableHead className="text-white/60">Contact</TableHead>
                <TableHead className="text-white/60">Date of birth</TableHead>
                {showArchived && <TableHead className="text-white/60">Status</TableHead>}
                <TableHead className="text-white/60 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {patients.map((patient) => (
                <TableRow key={patient.id} data-testid={`patient-row-${patient.id}`}>
                  <TableCell className="text-white/90">{patient.first_name} {patient.last_name}</TableCell>
                  <TableCell className="text-white/70">
                    {patient.email || "—"}{patient.email && patient.phone ? " · " : ""}{patient.phone || ""}
                  </TableCell>
                  <TableCell className="text-white/70">{patient.date_of_birth || "—"}</TableCell>
                  {showArchived && (
                    <TableCell className="text-white/70">{patient.archived ? "Archived" : "Active"}</TableCell>
                  )}
                  <TableCell className="text-right">
                    {patient.archived ? (
                      <Button className="ghost-btn text-xs" onClick={() => restore(patient)} data-testid={`restore-patient-${patient.id}`}>
                        <ArchiveRestore size={14} /> Restore
                      </Button>
                    ) : (
                      <>
                        <Button className="ghost-btn text-xs" onClick={() => openEdit(patient)} data-testid={`edit-patient-${patient.id}`}>
                          <Pencil size={14} /> Edit
                        </Button>
                        <Button className="ghost-btn text-xs" onClick={() => archive(patient)} data-testid={`archive-patient-${patient.id}`}>
                          <Archive size={14} /> Archive
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">{editingId ? "Edit Patient" : "Add Patient"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70">First name</Label>
                <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Last name</Label>
                <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-white/70">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
            <div>
              <Label className="text-white/70">Date of birth</Label>
              <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/70">Notes</Label>
              <Textarea maxLength={5000} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white/5 border-white/10 text-white" />
            </div>
            {formError && <div className="form-error">{formError}</div>}
            <DialogFooter>
              <Button type="button" className="ghost-btn" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="primary-btn" disabled={saving} data-testid="save-patient-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PatientsTab;
